import { ReactNode } from 'react'

import * as Icons from '@fortawesome/pro-regular-svg-icons'
import { parsePhoneNumber } from 'awesome-phonenumber'
import isNil from 'lodash.isnil'

import {
  AutomationActionType,
  EntityDetailsFragment,
  EntityLayerType,
  FileFragment,
  RecordTaskGroupType,
  State,
  UserDataFragment,
  ViolationCode,
  ViolationMailerTitle,
  Visibility,
} from '../../types/graphql'
import { StateAbbreviationMap } from '../constants/states'

export const getInitials = (
  firstName: string | undefined | null,
  lastName: string | undefined | null
) => {
  if (!firstName && !lastName) {
    return ''
  }
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
}

export const getInitialsOneStr = (str: string) => {
  const split = str.split(' ')
  const first = split?.[0]?.[0] || ''
  const second = split?.[1]?.[0] || ''
  return `${first}${second}`.toUpperCase()
}

export const formatName = (
  firstName: string | undefined | null,
  lastName: string | undefined | null
) => `${firstName || ''}${firstName && lastName ? ' ' : ''}${lastName || ''}`

export const formatViolationCode = (violationCode: Partial<ViolationCode>) => {
  const res = `${violationCode.corpus} ${violationCode.sectionName} (${violationCode.title})`
  return removeLinebreaks(res).trim()
}

export const getUserInitials = (user?: UserDataFragment | null) =>
  getInitials(user?.firstName, user?.lastName)

export const openUrlInNewTab = (url: string) => window.open(url, '_blank')

export const sortViolationMailerTitles = <TMailer extends Pick<ViolationMailerTitle, 'title'>>(
  violationMailerTitles: TMailer[]
) => {
  return [...violationMailerTitles].sort((mt1: TMailer, mt2: TMailer) => {
    if (mt1.title > mt2.title) {
      return 1
    }
    return -1
  })
}

export const removeLinebreaks = (str: string) => {
  return str.replace(/[\r\n]+/gm, '')
}

export const isNumber = (number?: number | string | null) =>
  number !== undefined && number !== null && typeof number === 'number'

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!bytes) {
    return ''
  }
  if (!+bytes) {
    return '0 Bytes'
  }
  const c = 0 > decimals ? 0 : decimals,
    d = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${parseFloat((bytes / Math.pow(1024, d)).toFixed(c))} ${
    ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][d]
  }`
}

export const removeElementAtIndex = (array: any[], index: number) => {
  return [...array].filter((_: any, elementIndex: number) => index !== elementIndex)
}

const appendUSIntlCode = (phoneNumber: string) =>
  phoneNumber.startsWith('+1') ? phoneNumber : `+1${phoneNumber}`

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) {
    return ''
  }
  return (
    parsePhoneNumber(appendUSIntlCode(phoneNumber), {
      regionCode: 'US',
    })?.number?.national ?? phoneNumber
  )
}

export const parsePhoneDigits = (value: string): string => value.replace(/\D/g, '').slice(0, 10)

export const phoneToE164 = (digits: string): string => (digits ? `+1${digits}` : '')

export const formatPhoneInput = (value: string): string => {
  const digits = parsePhoneDigits(value)
  if (digits.length <= 3) {
    return digits
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

interface ViolationHistoryPartial {
  violationTypeOther?: string | null
  violationTypes?:
    | {
        id: number
        name: string
      }[]
    | null
}

export const getStringifiedViolationTypeForViolation = (
  violationHistory: ViolationHistoryPartial | null | undefined
) => {
  if (violationHistory?.violationTypes?.length) {
    return violationHistory.violationTypes.map((t) => t.name).join(', ')
  }
  return 'Other'
}

export const getStringifiedViolationTypesForViolation = (
  violationHistory: ViolationHistoryPartial
): string[] => {
  if (violationHistory.violationTypes?.length) {
    return violationHistory.violationTypes.map((t) => t.name)
  }
  return ['Other']
}

export const getArray0ToN = (number: number): number[] => {
  return [...Array(number).keys()]
}

export const formatAddress = (inputString?: string): string => {
  const words: string[] = inputString?.split(' ') || []

  const formattedWords = words.map((word) => {
    if (Object.values(StateAbbreviationMap).includes(word)) {
      return word.toUpperCase()
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  })

  return formattedWords.map((str) => str.trim()).join(' ')
}

export const formatUserName = (
  user:
    | {
        firstName?: string | null | undefined
        lastName?: string | null | undefined
        emailAddress?: string | null | undefined
      }
    | null
    | undefined
): string => {
  if (user?.firstName && user?.lastName) {
    return formatName(user.firstName, user.lastName)
  }
  return user?.emailAddress || '-'
}

export const getAvg = (nums: number[]): number => {
  if (nums.length === 0) {
    return 0
  }
  const total = nums.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
  return total / nums.length
}

export const formatCityState = (
  city: string | null | undefined,
  state: State | null | undefined
): string => {
  if (!state) {
    return city ?? '-'
  }
  return `${city}, ${StateAbbreviationMap[state]}`
}

export const isUserGov = (user?: UserDataFragment): boolean => Boolean(user && user.organizationId)
export const isUserCitizen = (user?: UserDataFragment): boolean =>
  Boolean(!user || !user.organizationId)

export const mapStrsToInts = (strs: string[]): number[] => {
  return strs.map((a) => parseInt(a))
}

export const getInt = (value: string | number): number => {
  return typeof value === 'number' ? value : parseInt(value)
}

export const areArraysEqual = (arr1: any[], arr2: any[]): boolean => {
  // Check if both arrays have the same length
  if (arr1.length !== arr2.length) {
    return false
  }

  // Convert the arrays to sets
  const set1 = new Set(arr1)
  const set2 = new Set(arr2)

  // Compare the sets
  for (const num of set1) {
    if (!set2.has(num)) {
      return false
    }
  }

  return true
}

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export enum ListEnding {
  AND = 'and',
  OR = 'or',
}
interface FormatListSentenceProps {
  words: string[]
  hideFinalPeriod: boolean
  finalItemDelimiter: ListEnding
}

// DUPLICATED ON BE
export const formatListSentence = ({
  words,
  hideFinalPeriod,
  finalItemDelimiter,
}: FormatListSentenceProps): string => {
  const length = words.length
  const finalPeriod = hideFinalPeriod ? '' : '.'

  if (length === 0) {
    return ''
  }

  if (length === 1) {
    return `${words[0]}${finalPeriod}`
  }

  if (length === 2) {
    return `${words[0]} ${finalItemDelimiter} ${words[1]}${finalPeriod}`
  }

  const last = words[length - 1]
  const others = words.slice(0, length - 1).join(', ')

  return `${others}, ${finalItemDelimiter} ${last}${finalPeriod}`
}

export const RecordTaskGroupTypeToRecordTaskGroupNameMap: Record<RecordTaskGroupType, string> = {
  AddToMeeting: 'Add to Meeting',
  ApplicationReview: 'Application Review',
  ApplicationReviewMultiple: 'Application Review',
  ChangeStatus: 'Change Status',
  Checklist: 'Checklist',
  CreateChildRecord: 'Create Child Record',
  CreateRequestedRecords: 'Submit Additional Records',
  Custom: 'Custom',
  DocumentGeneration: 'Generate Document',
  Expiration: 'Set Expiration Date',
  Inspections: 'Inspections',
  Issue: 'Issue Record',
  LogMeetingResult: 'Log Meeting Result',
  Migrate: 'Migrate',
  Payment: 'Payment',
  PlanReview: 'Plan Review',
  Renewal: 'Renewal',
  RequestRecords: 'Request Child Records',
  ScheduleWorkflow: 'Schedule Workflow',
  SearchForNearbyProperties: 'Search for Nearby Properties',
  SendNotification: 'Send Email',
  SkipToStep: 'Skip to Step',
}

export const AutomationActionTypeNameMap: Record<AutomationActionType, string> = {
  AddDraftRecordTaskFee: 'Add draft fee',
  AddWorkflowStep: 'Add workflow step at end of workflow',
  AddWorkflowStepUnordered: 'Add workflow step (unordered)',
  BeginWorkflow: 'Begin workflow',
  CancelRecord: 'Cancel record',
  CloseRecord: 'Close record',
  Notification: 'Notification',
  StatusChange: 'Change status',
  UpdateExpiration: 'Update expiration',
}

export const trimFinalPeriods = (inputString: ReactNode): ReactNode => {
  // Use a regular expression to match one or more periods at the end of the string and replace them with an empty string.
  if (typeof inputString === 'string') {
    return inputString.replace(/\.*$/, '')
  }
  return inputString
}

export const filterNumeric = (str: string): string => {
  return str.replace(/[^\d.]/g, '')
}

export const sleep = (milliseconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })
}

export function reorderArray<T>(
  arr: T[] | readonly T[],
  originalIndex: number,
  newIndex: number
): T[] {
  const newArr = [...arr]
  reorderArrayInPlace(newArr, originalIndex, newIndex)
  return newArr
}

export function reorderArrayInPlace<T>(arr: T[], originalIndex: number, newIndex: number) {
  const [movedElement] = arr.splice(originalIndex, 1)
  // Insert the removed element at the 'newIndex'
  if (movedElement) {
    arr.splice(newIndex, 0, movedElement)
  }
}

export function findIndicesOfObjectsInNewArray<T extends { id: number }>(
  oldData: T[],
  newData: T[]
): number[] {
  const indices: number[] = []

  for (let i = 0; i < oldData.length; i++) {
    const obj1 = oldData[i]
    if (!obj1) {
      continue
    }

    const indexInArr2 = newData.findIndex((obj2) => obj1.id === obj2.id)

    if (indexInArr2 !== -1) {
      indices.push(indexInArr2)
    }
  }

  return indices
}

export const maybeMakeString = (value?: number | boolean | null): string | undefined => {
  return !isNil(value) ? `${value}` : undefined
}

export const maybeMakeInt = (value?: number | string | null): number => {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'undefined' || value === null) {
    return NaN
  }
  return parseInt(value)
}

interface OnAddedOrDeletedElementParams {
  arr1: number[]
  arr2: number[]
  onDeleted: (deleted: number) => Promise<void>
  onAdded: (added: number) => Promise<void>
}
export const onAddedOrDeletedElement = async (params: OnAddedOrDeletedElementParams) => {
  const { arr1, arr2, onDeleted, onAdded } = params
  const firstSet = new Set(arr1)
  const secondSet = new Set(arr2)

  for (const element of secondSet) {
    if (!firstSet.has(element)) {
      await onAdded(element)
    }
  }

  for (const element of firstSet) {
    if (!secondSet.has(element)) {
      await onDeleted(element)
    }
  }
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules
const OrdinalSuffixes = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th',
  zero: 'th', // not returned for ordinal rules for English, just a placeholder value to appease the typing
  many: 'th', // not returned for ordinal rules for English, just a placeholder value to appease the typing
}
export const getOrdinalNumber = (value: number) => {
  const ordinalPluralRules = new Intl.PluralRules('en', { type: 'ordinal' })
  const suffix = OrdinalSuffixes[ordinalPluralRules.select(value)]
  return `${value}${suffix}`
}

export const getIcon = (icon: string) => Icons[icon as keyof typeof Icons] as Icons.IconDefinition

export function areSetsEqual<T>(setA: Set<T>, setB: Set<T>): boolean {
  if (setA.size !== setB.size) {
    return false
  }
  for (const elem of setA) {
    if (!setB.has(elem)) {
      return false
    }
  }
  return true
}

export const isHtmlString = (str: string) => {
  // Regular expression to detect HTML tags
  const htmlRegex = /<[^>]*>/
  return htmlRegex.test(str)
}

export const isFunctionAsync = <
  T extends (() => void | Promise<void> | Promise<unknown>) & {
    [Symbol.toStringTag]?: string
  },
>(
  fn: T
) => {
  try {
    const isAsync = fn[Symbol.toStringTag] === 'AsyncFunction'
    return isAsync
  } catch {
    return false
  }
}

export function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) {
    return true
  }

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
    return false
  }

  const keysA = Object.keys(obj1)
  const keysB = Object.keys(obj2)

  if (keysA.length !== keysB.length) {
    return false
  }

  for (const key of keysA) {
    if (!keysB.includes(key)) {
      return false
    }

    if (!deepEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}

export const filterNil = <T>(arr: (T | null | undefined)[]): T[] =>
  arr.flatMap((i) => (isNil(i) ? [] : [i]))

export const filterNilOrEmptyString = (arr: (string | null | undefined)[]): string[] =>
  arr.flatMap((i) => (isNil(i) || !i ? [] : [i]))

export const VisibilityLabelMap: Record<Visibility, string> = {
  Hidden: 'Hidden',
  Staff: 'Staff Only',
  StaffAndCitizens: 'Staff and Citizens',
}

export const formatFileCreatedBy = (file: FileFragment): string => {
  if (file.importedAt) {
    return 'GovWell System'
  }
  return formatUserName(file.createdByUser)
}

type PartialRecordPlansSetReviewResult = { isPassing: boolean; isDefault: boolean }
type FilterRecordPlansSetReviewResultsParams<T extends PartialRecordPlansSetReviewResult> = {
  results: T[]
  isPassing: boolean
}
export function filterRecordPlansSetReviewResults<T extends PartialRecordPlansSetReviewResult>({
  results,
  isPassing,
}: FilterRecordPlansSetReviewResultsParams<T>) {
  return results
    .filter((r) => r.isPassing === isPassing)
    .sort((a, b) => (a.isDefault ? -1 : 1) - (b.isDefault ? -1 : 1))
}

export const formatUserLabel = (user: UserDataFragment) => {
  const name = formatUserName(user)
  const phoneRaw = user.cellPhone || user.officePhone
  const phone = phoneRaw ? formatPhoneNumber(phoneRaw) : ''
  return [name, phone, user.emailAddress].filter((item) => !!item).join(' | ')
}

// Sort users by name / phone / email
export const compareUsersByLabel = (a: UserDataFragment, b: UserDataFragment) =>
  formatUserLabel(a).localeCompare(formatUserLabel(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  })

type SortUsersByGroupThenByLabelParams = {
  users: UserDataFragment[]
  /** Numeric rank controlling group display order. Lower ranks appear first.
   *  Users without a group sort after all ranked groups. */
  getGroupSortRank?: (user: UserDataFragment) => number | undefined
  /** Display label for the user's group. Used as a fallback tiebreaker when two groups
   *  share the same sort rank (alphabetical comparison). */
  getGroupLabel?: (user: UserDataFragment) => string | undefined
}

/**
 * Sort users with a three-level priority:
 *  1. Sort groups by rank (defined by caller)
 *  2. Compare groups by label (alphabetical tiebreaker for same-rank groups)
 *  3. Sort users byname / phone / email
 *
 * Returns a new sorted array; does not mutate the input.
 */
export const sortUsersByGroupThenByLabel = ({
  users,
  getGroupSortRank,
  getGroupLabel,
}: SortUsersByGroupThenByLabelParams) => {
  const compareUsersByGroupThenByLabel = (userA: UserDataFragment, userB: UserDataFragment) => {
    const groupSortRankA = getGroupSortRank?.(userA) ?? Number.MAX_SAFE_INTEGER
    const groupSortRankB = getGroupSortRank?.(userB) ?? Number.MAX_SAFE_INTEGER

    if (groupSortRankA !== groupSortRankB) {
      return groupSortRankA - groupSortRankB
    }

    const groupA = getGroupLabel?.(userA) ?? ''
    const groupB = getGroupLabel?.(userB) ?? ''

    const groupComparison = groupA.localeCompare(groupB, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
    if (groupComparison !== 0) {
      return groupComparison
    }

    return compareUsersByLabel(userA, userB)
  }

  return [...users].sort(compareUsersByGroupThenByLabel)
}

export function dedupeObjectsById<
  T extends {
    id: string | number
  },
>(inputArray: T[]): T[] {
  const uniqueMap = new Map<string | number, T>()

  inputArray.forEach((item) => {
    uniqueMap.set(item.id, item) // This will overwrite any duplicate id
  })

  return Array.from(uniqueMap.values())
}

/**

Explanation:
^(https?:\/\/)?: Optional http:// or https:// at the start.
([\w-]+(\.[\w-]+)+): Matches domain names like example.com or sub.domain.co.uk.
(:[0-9]{1,5})?: Optional port number (like :8080).
(\/[^\s]*)?: Optional path and query string.
/i: Case-insensitive.

✅ Examples that match
https://example.com
http://localhost:3000/path
example.com/some/path
sub.domain.co.uk:8080/path?query=1

❌ Examples that do not match
ftp://example.com (no FTP support)
example (not a domain)
://missing.scheme.com

 */
export const isValidUrl = (str: string): boolean => {
  const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]{1,5})?(\/[^\s]*)?$/i
  return urlRegex.test(str)
}

export const identity = <T>(value: T): T => value

export const serializeMap = <TKey, TValue>(map: Map<TKey, TValue>): string => {
  return JSON.stringify(Array.from(map.entries()))
}

export const deserializeMap = <TKey, TValue>(str: string): Map<TKey, TValue> => {
  try {
    const arr = JSON.parse(str)
    if (!Array.isArray(arr)) {
      return new Map()
    }
    return new Map(arr)
  } catch {
    return new Map()
  }
}

export const getEntityPageTitle = (
  entity: EntityDetailsFragment,
  layerType: EntityLayerType,
  organizationHasMultipleEntityLayers: boolean
): string => {
  if (organizationHasMultipleEntityLayers) {
    return `${layerType === 'Address' ? 'Address' : 'Parcel'} ${entity.name}`
  }
  const addressField = entity.latestVersion?.fields?.find(
    (f) => f.entityFieldTemplate?.isAddressDisplayName
  )
  const parcelField = entity.latestVersion?.fields?.find(
    (f) => f.entityFieldTemplate?.isParcelDisplayName
  )
  if (addressField && parcelField) {
    return `${addressField.stringifiedValue} (Parcel: ${parcelField.stringifiedValue})`
  }
  if (addressField) {
    return addressField.stringifiedValue ?? ''
  }
  if (parcelField) {
    return parcelField.stringifiedValue ?? ''
  }
  return ''
}
